import { useCallback, useLayoutEffect, useRef, type MutableRefObject, type RefObject } from 'react';

import {
    CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS,
    CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS,
    syncTableWrapperScrollFromChart,
} from './packArchitectureTable.helpers';

type UsePackArchitectureChartToTableScrollSyncParams = {
    chartScrollRef: RefObject<HTMLDivElement | null>;
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef: MutableRefObject<boolean>;
    suppressChartToTableSyncRef: MutableRefObject<boolean>;
    chartColumnCount: number;
    isChartPaginatedLoading: boolean;
    isTablePaginatedLoading: boolean;
    tableLoadedColumnCount: number;
    /** Re-attach when chart columns change or table first mounts — not on table column pagination. */
    tableSyncKey: number;
};

/**
 * One-way chart → table scroll sync (Price Ladder pattern).
 * useLayoutEffect + scrollTo({ behavior: 'auto' }) keeps table in step while chart scrolls.
 */
export function usePackArchitectureChartToTableScrollSync({
    chartScrollRef,
    tableWrapperRef,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
    chartColumnCount,
    isChartPaginatedLoading,
    isTablePaginatedLoading,
    tableLoadedColumnCount,
    tableSyncKey,
}: UsePackArchitectureChartToTableScrollSyncParams) {
    const syncGuardTimeoutRef = useRef<number | null>(null);
    const previousChartColumnCountRef = useRef(chartColumnCount);
    const previousTableLoadedColumnCountRef = useRef<number | null>(null);

    const syncTableFromChart = useCallback(() => {
        if (suppressChartToTableSyncRef.current) return;

        const chartElement = chartScrollRef.current;
        const tableWrapper = tableWrapperRef.current;
        if (!chartElement || !tableWrapper) return;

        if (syncGuardTimeoutRef.current !== null) {
            window.clearTimeout(syncGuardTimeoutRef.current);
        }

        isSyncingTableFromChartRef.current = true;
        syncTableWrapperScrollFromChart(chartElement, tableWrapper);
        syncGuardTimeoutRef.current = window.setTimeout(() => {
            isSyncingTableFromChartRef.current = false;
            syncGuardTimeoutRef.current = null;
        }, CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS);
    }, [chartScrollRef, isSyncingTableFromChartRef, suppressChartToTableSyncRef, tableWrapperRef]);

    useLayoutEffect(() => {
        const chartElement = chartScrollRef.current;
        if (!chartElement) return undefined;

        const previousTableColumns = previousTableLoadedColumnCountRef.current;
        const didTableAppendColumns =
            previousTableColumns !== null && tableLoadedColumnCount > previousTableColumns;
        previousTableLoadedColumnCountRef.current = tableLoadedColumnCount;

        chartElement.addEventListener('scroll', syncTableFromChart, { passive: true });

        if (!didTableAppendColumns && !isTablePaginatedLoading && !suppressChartToTableSyncRef.current) {
            syncTableFromChart();
        }

        return () => {
            chartElement.removeEventListener('scroll', syncTableFromChart);
            if (syncGuardTimeoutRef.current !== null) {
                window.clearTimeout(syncGuardTimeoutRef.current);
                syncGuardTimeoutRef.current = null;
            }
        };
    }, [
        chartScrollRef,
        isTablePaginatedLoading,
        suppressChartToTableSyncRef,
        syncTableFromChart,
        tableLoadedColumnCount,
        tableSyncKey,
    ]);

    useLayoutEffect(() => {
        const didAppendColumns = chartColumnCount > previousChartColumnCountRef.current;
        previousChartColumnCountRef.current = chartColumnCount;

        if (!didAppendColumns || isChartPaginatedLoading || suppressChartToTableSyncRef.current) {
            return undefined;
        }

        const timeoutIds = CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS.map((delayMs) =>
            window.setTimeout(() => {
                if (!suppressChartToTableSyncRef.current) {
                    syncTableFromChart();
                }
            }, delayMs),
        );

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [chartColumnCount, isChartPaginatedLoading, suppressChartToTableSyncRef, syncTableFromChart]);
}
